/* eslint-disable import/no-unused-modules */
import { createContext, useContext, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Handle, Position, useReactFlow } from "reactflow";

export const FeatureDocsContext = createContext({});

// ─── Shared styles ────────────────────────────────────────────────────────────

const base = {
  borderRadius: 12,
  fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
  fontSize: 13,
  boxShadow: "0 2px 10px rgba(0,0,0,0.08)",
};

const stepLabel = {
  fontSize: 9,
  fontWeight: 700,
  letterSpacing: "0.08em",
  textTransform: "uppercase",
  marginBottom: 6,
  opacity: 0.55,
};

const handle = {
  background: "transparent",
  border: "none",
  width: 8,
  height: 8,
};

// ─── HeaderNode ───────────────────────────────────────────────────────────────

export function HeaderNode({ data }) {
  const { asin, store } = data;
  return (
    <div
      style={{
        ...base,
        width: 320,
        background: "linear-gradient(135deg, #1e3a5f 0%, #2563eb 100%)",
        color: "#fff",
        padding: "14px 18px",
        border: "2px solid #1d4ed8",
      }}
    >
      <Handle type="source" position={Position.Bottom} style={handle} />
      <div style={stepLabel}>EVALUATED ASIN</div>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          marginBottom: 10,
        }}
      >
        <div
          style={{
            width: 20,
            height: 20,
            borderRadius: "50%",
            background: "#9c27b0",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 12,
            flexShrink: 0,
          }}
        >
          🤖
        </div>
        <div>
          <div
            style={{ fontWeight: 700, fontSize: 15, letterSpacing: "0.04em" }}
          >
            Iris Rule Engine
          </div>
          <div style={{ opacity: 0.75, fontSize: 11 }}>
            Inventory decision engine
          </div>
        </div>
      </div>
      <div
        style={{
          background: "rgba(255,255,255,0.12)",
          borderRadius: 8,
          padding: "8px 12px",
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: "6px 12px",
        }}
      >
        <Kv label="ASIN" value={asin} mono />
        <Kv label="Store" value={store} />
      </div>
    </div>
  );
}

// ─── RuleNode ─────────────────────────────────────────────────────────────────

export function RuleNode({ data }) {
  const {
    label,
    sub,
    icon,
    conditions = [],
    formulas = [],
    humanReadable = [],
    features,
    hit,
    isWinner,
  } = data;

  const bg = isWinner ? "#dcfce7" : hit ? "#f0fdf4" : "#f8fafc";
  const border = isWinner ? "#15803d" : hit ? "#22c55e" : "#e2e8f0";
  const textColor = isWinner ? "#14532d" : hit ? "#166534" : "#94a3b8";
  const shadow = isWinner
    ? "0 0 0 3px rgba(34,197,94,0.25), 0 2px 10px rgba(0,0,0,0.08)"
    : base.boxShadow;

  return (
    <div
      style={{
        ...base,
        width: 260,
        background: bg,
        border: `2px solid ${border}`,
        padding: "12px 14px",
        boxShadow: shadow,
        position: "relative",
      }}
    >
      <Handle type="target" position={Position.Top} style={handle} />
      <Handle type="source" position={Position.Bottom} style={handle} />

      {isWinner && (
        <div
          style={{
            position: "absolute",
            top: -10,
            right: 10,
            background: "#15803d",
            color: "#fff",
            fontSize: 10,
            fontWeight: 700,
            padding: "2px 8px",
            borderRadius: 999,
            letterSpacing: "0.05em",
          }}
        >
          WINNER
        </div>
      )}

      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 6,
          marginBottom: 8,
        }}
      >
        <span style={{ fontSize: 18 }}>{icon}</span>
        <div>
          <div
            style={{
              fontWeight: 700,
              fontSize: 12,
              color: textColor,
              lineHeight: 1.2,
            }}
          >
            {label}
          </div>
          <div
            style={{
              fontSize: 11,
              color: textColor,
              opacity: 0.8,
              lineHeight: 1.2,
            }}
          >
            {sub}
          </div>
        </div>
      </div>

      <div
        style={{
          borderTop: `1px solid ${border}`,
          paddingTop: 8,
          display: "flex",
          flexDirection: "column",
          gap: 4,
        }}
      >
        <StatusBadge hit={hit} />

        <RuleSection title="Conditions" accent={textColor} border={border}>
          {conditions.length > 0 ? (
            conditions.map((cond, i) => (
              <div
                key={i}
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 2,
                  marginBottom: 4,
                }}
              >
                <div
                  style={{
                    fontSize: 10,
                    color: textColor,
                    lineHeight: 1.3,
                    overflowWrap: "break-word",
                  }}
                >
                  <TaggedText text={cond.label} features={features} />
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                  <span
                    style={{ fontSize: 11, fontWeight: 700, color: textColor }}
                  >
                    {formatConditionValue(cond.actual)}
                  </span>
                  <span
                    style={{
                      fontSize: 10,
                      color: cond.met ? "#16a34a" : "#ef4444",
                      fontWeight: 600,
                    }}
                  >
                    {cond.met ? "✓" : "✗"}
                  </span>
                </div>
              </div>
            ))
          ) : (
            <div style={{ fontSize: 10, color: textColor, opacity: 0.5 }}>
              Not evaluated
            </div>
          )}
        </RuleSection>

        {formulas.length > 0 && (
          <RuleSection title="Formulas" accent={textColor} border={border}>
            {formulas.map((f, i) => (
              <div
                key={i}
                style={{
                  fontSize: 10,
                  color: textColor,
                  lineHeight: 1.4,
                  overflowWrap: "break-word",
                  marginBottom: 3,
                  fontFamily: "monospace",
                }}
              >
                <TaggedText text={f} features={features} />
              </div>
            ))}
          </RuleSection>
        )}

        {humanReadable.length > 0 && (
          <RuleSection title="Explanation" accent={textColor} border={border}>
            {humanReadable.map((line, i) => (
              <div
                key={i}
                style={{
                  fontSize: 10,
                  color: textColor,
                  lineHeight: 1.4,
                  overflowWrap: "break-word",
                  marginBottom: 3,
                }}
              >
                <TaggedText text={line} features={features} />
              </div>
            ))}
          </RuleSection>
        )}
      </div>
    </div>
  );
}

// ─── Feature tag parsing ──────────────────────────────────────────────────────

function fmtFeatureVal(key, val) {
  if (val == null) return "—";
  if (typeof val === "boolean") return val ? "Yes" : "No";
  const k = key.toLowerCase();
  if (k.includes("price") || k.includes("cost") || k.includes("budget"))
    return `$${Number(val).toFixed(2)}`;
  if (k.includes("acos")) return `${(val * 100).toFixed(1)}%`;
  if (typeof val === "number")
    return Number.isInteger(val) ? String(val) : val.toFixed(2);
  return String(val);
}

export function FeatureTag({ name, features }) {
  const [pos, setPos] = useState(null);
  const featureDocs = useContext(FeatureDocsContext);
  const value = features?.[name];
  const shortDesc = featureDocs.features?.[name]?.short_description;

  return (
    <span
      onMouseMove={(e) => setPos({ x: e.clientX, y: e.clientY })}
      onMouseLeave={() => setPos(null)}
      onClick={(e) => {
        e.stopPropagation();
        window.dispatchEvent(
          new CustomEvent("iris:feature-click", { detail: { name } })
        );
      }}
      onPointerDown={(e) => e.stopPropagation()}
      style={{ color: "#60a5fa", fontWeight: 700, cursor: "pointer" }}
    >
      {name}
      {pos &&
        createPortal(
          <div
            style={{
              position: "fixed",
              top: pos.y - (shortDesc ? 58 : 38),
              left: pos.x,
              transform: "translateX(-50%)",
              background: "#1e3a5f",
              color: "#fff",
              padding: "7px 11px",
              borderRadius: 7,
              fontSize: 11,
              fontFamily: "SFMono-Regular, Consolas, monospace",
              whiteSpace: "nowrap",
              zIndex: 9999,
              pointerEvents: "none",
              boxShadow: "0 4px 12px rgba(0,0,0,0.25)",
              maxWidth: 300,
            }}
          >
            <div>
              {name}: <strong>{fmtFeatureVal(name, value)}</strong>
            </div>
            {shortDesc && (
              <div
                style={{
                  marginTop: 4,
                  fontSize: 10,
                  fontFamily:
                    '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
                  fontWeight: 400,
                  opacity: 0.8,
                  whiteSpace: "normal",
                  lineHeight: 1.4,
                }}
              >
                {shortDesc}
              </div>
            )}
          </div>,
          document.body
        )}
    </span>
  );
}

function RuleTag({ name }) {
  const { getNodes, fitView } = useReactFlow();

  const handleClick = (e) => {
    e.stopPropagation();
    const node = getNodes().find((n) => n.data?.ruleId === name);
    if (node)
      fitView({
        nodes: [{ id: node.id }],
        duration: 400,
        padding: 0.5,
        maxZoom: 1.5,
      });
  };

  return (
    <span
      onClick={handleClick}
      onPointerDown={(e) => e.stopPropagation()}
      style={{
        color: "#8b5cf6",
        fontWeight: 700,
        cursor: "pointer",
        textDecoration: "underline",
        textDecorationColor: "rgba(139,92,246,0.4)",
      }}
    >
      {name}
    </span>
  );
}

export function TaggedText({ text, features, inFlow = true }) {
  if (!text) return null;
  const parts = text.split(/(<feature>.*?<\/feature>|<rule>.*?<\/rule>)/g);
  return (
    <>
      {parts.map((part, i) => {
        const featureMatch = part.match(/^<feature>(.*?)<\/feature>$/);
        if (featureMatch)
          return (
            <FeatureTag key={i} name={featureMatch[1]} features={features} />
          );
        const ruleMatch = part.match(/^<rule>(.*?)<\/rule>$/);
        if (ruleMatch)
          return inFlow ? (
            <RuleTag key={i} name={ruleMatch[1]} />
          ) : (
            <span key={i} style={{ color: "#8b5cf6", fontWeight: 700 }}>
              {ruleMatch[1]}
            </span>
          );
        return <span key={i}>{part}</span>;
      })}
    </>
  );
}

function RuleSection({ title, accent, border, children }) {
  const [open, setOpen] = useState(true);
  useEffect(() => {
    const handler = (e) => setOpen(!e.detail.collapsed);
    window.addEventListener("iris:toggle-all", handler);
    return () => window.removeEventListener("iris:toggle-all", handler);
  }, []);
  return (
    <div style={{ marginTop: 4 }}>
      <button
        className="nopan"
        onClick={(e) => {
          e.stopPropagation();
          setOpen((o) => !o);
        }}
        onPointerDown={(e) => e.stopPropagation()}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 4,
          width: "100%",
          background: "none",
          border: "none",
          padding: "3px 0",
          cursor: "pointer",
          color: accent,
          fontSize: 10,
          fontWeight: 700,
          letterSpacing: "0.06em",
          textTransform: "uppercase",
          opacity: 0.65,
        }}
      >
        <span style={{ fontSize: 9, lineHeight: 1 }}>{open ? "▾" : "▸"}</span>
        {title}
      </button>
      {open && (
        <div
          style={{
            paddingLeft: 8,
            borderLeft: `2px solid ${border}`,
            marginTop: 2,
          }}
        >
          {children}
        </div>
      )}
    </div>
  );
}

// ─── PrioritizeNode ───────────────────────────────────────────────────────────

export function PrioritizeNode({ data }) {
  const { winnerLabel, note, isDoNothing, features } = data;

  const bg = isDoNothing ? "#f8fafc" : "#eff6ff";
  const border = isDoNothing ? "#e2e8f0" : "#3b82f6";
  const headColor = isDoNothing ? "#94a3b8" : "#1d4ed8";
  const textColor = isDoNothing ? "#94a3b8" : "#1e40af";

  return (
    <div
      style={{
        ...base,
        width: 320,
        background: bg,
        border: `2px solid ${border}`,
        padding: "12px 16px",
      }}
    >
      <Handle type="target" position={Position.Top} style={handle} />
      <Handle type="source" position={Position.Bottom} style={handle} />

      <div style={{ ...stepLabel, color: headColor }}>
        ⚖️ Rule prioritization
      </div>

      {isDoNothing ? (
        <div style={{ color: "#94a3b8", fontSize: 13, fontWeight: 600 }}>
          No active rules
        </div>
      ) : (
        <div
          style={{
            fontWeight: 700,
            fontSize: 13,
            color: textColor,
            marginBottom: 6,
          }}
        >
          <TaggedText text={winnerLabel} features={features} />
        </div>
      )}

      <div
        style={{
          fontSize: 11,
          color: textColor,
          opacity: 0.75,
          background: "rgba(59,130,246,0.07)",
          borderRadius: 6,
          padding: "4px 8px",
          marginTop: 4,
          lineHeight: 1.5,
        }}
      >
        <TaggedText text={note} features={features} />
      </div>
    </div>
  );
}

// ─── ActionNode ───────────────────────────────────────────────────────────────

export function ActionNode({ data }) {
  const { rawSteps, features, isDoNothing, isVeryLow, actionType } = data;

  const bg = isDoNothing ? "#f8fafc" : isVeryLow ? "#fef2f2" : "#fffbeb";
  const border = isDoNothing ? "#e2e8f0" : isVeryLow ? "#ef4444" : "#f59e0b";
  const headColor = isDoNothing ? "#94a3b8" : isVeryLow ? "#dc2626" : "#b45309";
  const textColor = isDoNothing ? "#94a3b8" : isVeryLow ? "#991b1b" : "#78350f";
  const cardBg = isDoNothing
    ? "rgba(0,0,0,0.03)"
    : isVeryLow
    ? "rgba(220,38,38,0.06)"
    : "rgba(217,119,6,0.07)";
  const cardBorder = isDoNothing
    ? "#e2e8f0"
    : isVeryLow
    ? "rgba(220,38,38,0.25)"
    : "rgba(217,119,6,0.3)";

  const actionIcon =
    { ppc: "📡", price: "🏷️", nothing: "✓" }[actionType] || "⚡";

  return (
    <div
      style={{
        ...base,
        width: 320,
        background: bg,
        border: `2px solid ${border}`,
        padding: "12px 16px",
        overflow: "hidden",
      }}
    >
      <Handle type="target" position={Position.Top} style={handle} />
      <Handle type="source" position={Position.Bottom} style={handle} />

      <div style={{ ...stepLabel, color: headColor }}>
        {actionIcon} Action logic
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        {rawSteps.map((step, i) => (
          <ActionStep
            key={i}
            step={step}
            textColor={textColor}
            cardBg={cardBg}
            cardBorder={cardBorder}
            features={features}
          />
        ))}
      </div>
    </div>
  );
}

function ActionStep({ step, textColor, cardBg, cardBorder, features }) {
  const [open, setOpen] = useState(false);
  const hasContent = !!(step.human_readable_description || step.calculation);
  useEffect(() => {
    const handler = (e) => {
      if (hasContent) setOpen(!e.detail.collapsed);
    };
    window.addEventListener("iris:toggle-all", handler);
    return () => window.removeEventListener("iris:toggle-all", handler);
  }, [hasContent]);

  return (
    <div
      style={{
        background: cardBg,
        border: `1px solid ${cardBorder}`,
        borderRadius: 7,
      }}
    >
      <button
        className="nopan"
        onClick={(e) => {
          e.stopPropagation();
          if (hasContent) setOpen((o) => !o);
        }}
        onPointerDown={(e) => e.stopPropagation()}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 5,
          width: "100%",
          background: "none",
          border: "none",
          padding: "6px 8px",
          cursor: hasContent ? "pointer" : "default",
          color: textColor,
          fontSize: 11,
          fontWeight: 600,
          textAlign: "left",
          lineHeight: 1.35,
        }}
      >
        {hasContent ? (
          <span style={{ fontSize: 9, opacity: 0.55, flexShrink: 0 }}>
            {open ? "▾" : "▸"}
          </span>
        ) : (
          <span style={{ fontSize: 9, opacity: 0.35, flexShrink: 0 }}>–</span>
        )}
        <span style={{ flex: 1 }}>
          <TaggedText text={step.label} features={features} />
        </span>
        {step.met != null && (
          <span
            style={{
              fontSize: 11,
              flexShrink: 0,
              color: step.met ? "#16a34a" : "#ef4444",
            }}
          >
            {step.met ? "✓" : "✗"}
          </span>
        )}
      </button>

      {open && hasContent && (
        <div
          style={{
            padding: "0 10px 8px 20px",
            display: "flex",
            flexDirection: "column",
            gap: 5,
            borderTop: `1px solid ${cardBorder}`,
          }}
        >
          {step.human_readable_description && (
            <div
              style={{
                fontSize: 10,
                color: textColor,
                lineHeight: 1.5,
                paddingTop: 6,
              }}
            >
              <TaggedText
                text={step.human_readable_description}
                features={features}
              />
            </div>
          )}
          {step.calculation && (
            <div
              style={{
                fontSize: 10,
                fontFamily: "SFMono-Regular, Consolas, monospace",
                color: textColor,
                lineHeight: 1.45,
                background: "rgba(0,0,0,0.06)",
                borderRadius: 5,
                padding: "5px 7px",
                wordBreak: "break-all",
                overflowWrap: "anywhere",
                whiteSpace: "pre-wrap",
              }}
            >
              <TaggedText text={step.calculation} features={features} />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── ResultNode ───────────────────────────────────────────────────────────────

export function ResultNode({ data }) {
  const {
    action,
    targetPrice,
    targetPpc,
    currentPrice,
    currentPpc,
    reasoning,
    reasoning2,
    features,
    isDoNothing,
    isVeryLow,
  } = data;

  const bg = isDoNothing
    ? "#f8fafc"
    : isVeryLow
    ? "linear-gradient(135deg, #7f1d1d, #dc2626)"
    : "linear-gradient(135deg, #1e3a5f 0%, #2563eb 100%)";

  const border = isDoNothing ? "#e2e8f0" : isVeryLow ? "#dc2626" : "#1d4ed8";
  const color = isDoNothing ? "#6b7280" : "#ffffff";
  const subColor = isDoNothing ? "#94a3b8" : "rgba(255,255,255,0.75)";

  const actionLabel =
    action?.replace("VERY LOW INVENTORY WARNING: ", "") ?? "Do Nothing";

  return (
    <div
      style={{
        ...base,
        width: 320,
        background: bg,
        border: `2px solid ${border}`,
        padding: "14px 18px",
        color,
      }}
    >
      <Handle type="target" position={Position.Top} style={handle} />

      <div style={{ ...stepLabel, opacity: 0.6, color }}>
        {isDoNothing ? "✓ result" : "📊 recommendation"}
      </div>

      <div
        style={{
          fontWeight: 700,
          fontSize: 14,
          marginBottom: 10,
          lineHeight: 1.3,
        }}
      >
        <TaggedText text={actionLabel} features={features} />
      </div>

      {!isDoNothing && (
        <div
          style={{
            background: "rgba(255,255,255,0.12)",
            borderRadius: 8,
            padding: "8px 12px",
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: "6px 12px",
          }}
        >
          {targetPpc != null && (
            <>
              <Kv
                label="Current PPC"
                value={`$${currentPpc?.toFixed(2)}/day`}
                light
              />
              <Kv
                label="Target PPC"
                value={`$${targetPpc.toFixed(2)}/day`}
                light
                highlight
              />
            </>
          )}
          {targetPrice != null && (
            <>
              <Kv
                label="Current price"
                value={`$${currentPrice?.toFixed(2)}`}
                light
              />
              <Kv
                label="Target price"
                value={`$${targetPrice.toFixed(2)}`}
                light
                highlight
              />
            </>
          )}
        </div>
      )}

      {isDoNothing && (
        <div style={{ fontSize: 12, color: subColor }}>
          Inventory in optimal range · No action required
        </div>
      )}

      {(reasoning || reasoning2) && (
        <div
          style={{
            marginTop: 10,
            borderTop: `1px solid rgba(255,255,255,0.15)`,
            paddingTop: 8,
            display: "flex",
            flexDirection: "column",
            gap: 6,
          }}
        >
          {reasoning && (
            <RuleSection
              title="Reasoning"
              accent={color}
              border="rgba(255,255,255,0.2)"
            >
              <div
                style={{
                  fontSize: 10,
                  color: subColor,
                  lineHeight: 1.5,
                  overflowWrap: "break-word",
                }}
              >
                <TaggedText text={reasoning} features={features} />
              </div>
            </RuleSection>
          )}
          {reasoning2 && (
            <RuleSection
              title="Reasoning 2"
              accent={color}
              border="rgba(255,255,255,0.2)"
            >
              <div
                style={{
                  fontSize: 10,
                  color: subColor,
                  lineHeight: 1.5,
                  overflowWrap: "break-word",
                }}
              >
                <TaggedText text={reasoning2} features={features} />
              </div>
            </RuleSection>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatConditionValue(v) {
  if (v == null) return "—";
  if (typeof v === "boolean") return v ? "yes" : "no";
  if (typeof v === "string") return v;
  if (typeof v === "number")
    return Number.isInteger(v) ? String(v) : v.toFixed(2);
  return String(v);
}

function Kv({ label, value, mono, light, highlight }) {
  return (
    <div>
      <div
        style={{
          fontSize: 9,
          fontWeight: 700,
          letterSpacing: "0.07em",
          textTransform: "uppercase",
          opacity: light ? 0.6 : 0.5,
          color: light ? "#fff" : "inherit",
          marginBottom: 1,
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontSize: 12,
          fontWeight: highlight ? 800 : 600,
          fontFamily: mono ? "monospace" : "inherit",
          color: highlight ? "#fbbf24" : light ? "#fff" : "inherit",
        }}
      >
        {value}
      </div>
    </div>
  );
}

function StatusBadge({ hit }) {
  return (
    <div
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 4,
        padding: "2px 8px",
        borderRadius: 999,
        fontSize: 10,
        fontWeight: 700,
        background: hit ? "#dcfce7" : "#f1f5f9",
        color: hit ? "#15803d" : "#94a3b8",
        border: `1px solid ${hit ? "#86efac" : "#e2e8f0"}`,
        marginTop: 2,
        alignSelf: "flex-start",
      }}
    >
      <span>{hit ? "✓" : "✗"}</span>
      <span>{hit ? "HIT" : "NO HIT"}</span>
    </div>
  );
}

// ─── nodeTypes export ─────────────────────────────────────────────────────────

export const nodeTypes = {
  headerNode: HeaderNode,
  ruleNode: RuleNode,
  prioritizeNode: PrioritizeNode,
  actionNode: ActionNode,
  resultNode: ResultNode,
};
